import React, { useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from 'framer-motion';
import { PhoneCallIcon, CalendarCheckIcon, MessageCircleIcon } from 'lucide-react';
import { Badge } from '../Badge';
import { Button } from '../Button';
function scrollTo(id: string) {
  document.querySelector(id)?.scrollIntoView({
    behavior: 'smooth'
  });
}
type OrbitBadge = {
  icon: React.ReactNode;
  label: string;
  /** angle in degrees around the core */
  angle: number;
  /** radius as a fraction of the stage */
  radius: number;
  accent: string;
};
const ORBIT_BADGES: OrbitBadge[] = [{
  icon: <PhoneCallIcon className="h-4 w-4" aria-hidden="true" data-id="element-145" data-mp="3mudjg1rajqc6" />,
  label: '電話に応対',
  angle: -90,
  radius: 1,
  accent: 'text-sky-blue'
}, {
  icon: <CalendarCheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-146" data-mp="1sgcldj3kf7d5" />,
  label: '予約を受付',
  angle: 30,
  radius: 1,
  accent: 'text-sage-green'
}, {
  icon: <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-147" data-mp="1njvystc0l126" />,
  label: '問い合わせに即答',
  angle: 150,
  radius: 1,
  accent: 'text-amber'
}];
/** Animated live-call waveform bars */
function Waveform() {
  const reduce = useReducedMotion();
  const bars = [0.35, 0.7, 0.45, 0.95, 0.6, 0.85, 0.4, 0.75, 0.5];
  return <div className="flex h-8 items-end gap-1" aria-hidden="true" data-id="element-148" data-mp="fcs2h94jyylc">
      {bars.map((h, i) => <motion.span key={i} className="w-1 rounded-full bg-sky-blue" style={{
      height: `${h * 100}%`
    }} animate={reduce ? undefined : {
      scaleY: [0.5, 1, 0.4, 0.9, 0.5]
    }} transition={{
      duration: 1.1 + i % 4 * 0.18,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: i * 0.06
    }} data-id="element-149" data-mp="jenbkzsq6g9o" data-mpi={i} />)}
    </div>;
}
export function CinematicHero() {
  const reduce = useReducedMotion();
  const stageRef = useRef<HTMLDivElement>(null);
  // Mouse-tracking parallax light + tilt
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, {
    stiffness: 120,
    damping: 18
  });
  const sy = useSpring(my, {
    stiffness: 120,
    damping: 18
  });
  const rotateX = useTransform(sy, [-0.5, 0.5], [8, -8]);
  const rotateY = useTransform(sx, [-0.5, 0.5], [-10, 10]);
  const lightX = useTransform(sx, [-0.5, 0.5], ['25%', '75%']);
  const lightY = useTransform(sy, [-0.5, 0.5], ['25%', '75%']);
  const lightBg = useTransform([lightX, lightY], ([x, y]) => `radial-gradient(600px circle at ${x} ${y}, rgba(116,167,201,0.18), transparent 60%)`);
  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (reduce) return;
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const handleLeave = () => {
    mx.set(0);
    my.set(0);
  };
  return <section id="top" className="relative w-full overflow-hidden bg-deep-navy" style={{
    backgroundColor: '#0a2d4d'
  }} data-id="element-150" data-mp="c8ztgowzcmzt">
      {/* solid-color glow blobs (no multi-color gradients) */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true" data-id="element-151" data-mp="381qm9ya2lit">
        <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-deep-navy opacity-60 blur-3xl" data-id="element-152" data-mp="86le4esis5da" />
        <div className="absolute right-0 top-1/3 h-80 w-80 rounded-full bg-sky-blue opacity-20 blur-3xl" data-id="element-153" data-mp="3pncpalbegc09" />
        <div className="absolute bottom-0 left-1/3 h-64 w-64 rounded-full bg-sage-green opacity-10 blur-3xl" data-id="element-154" data-mp="3ugfksyzqlvj3" />
      </div>

      {/* mouse-tracking radial light */}
      <motion.div className="pointer-events-none absolute inset-0" aria-hidden="true" style={{
      background: lightBg
    }} data-id="element-155" data-mp="3kojmpnxni5ev" />

      {/* film grain */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay" aria-hidden="true" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} data-id="element-156" data-mp="3bowjqxp9wrjp" />

      <div className="relative mx-auto grid max-w-6xl items-center px-4 py-20 sm:px-6 sm:py-28 lg:grid-cols-2 gap-[48px]" data-id="element-157" data-mp="3vbt7xsjfcogl">
        {/* Copy column */}
        <div className="text-center lg:text-left" data-id="element-158" data-mp="3qavrplhz64hq">
          <div className="flex justify-center lg:justify-start" data-id="element-159" data-mp="3m9to6sh0z69y">
            <Badge variant="sky" style={{
            color: '#74A7C9'
          }} data-id="element-160" data-mp="3mcg3xfe2ks39">
              横須賀の中小企業さまへ
            </Badge>
          </div>
          <h1 className="mt-5 font-heading font-bold text-white" data-id="element-161" data-mp="1kgw0tc60p5we">
            <span className="mt-3 block text-[35.5px] sm:text-4xl lg:text-5xl" data-id="element-162" data-mp="1fkorraibrea8">
              問い合わせや予約の対応で、本業が止まっていませんか？
            </span>
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/80 lg:mx-0" data-id="element-163" data-mp="1s2hnv6d90ssp">
            最短翌日から使える「AI受付スタッフ」が、電話・予約・問い合わせの対応を肩代わり。
            あなたが本業に集中できる環境を、伴走しながら整えます。
          </p>
          <div className="mt-9 flex flex-col items-center gap-3 sm:flex-row sm:justify-center lg:justify-start" data-id="element-164" data-mp="21t2kio9d3n8z">
            <Button variant="secondary" size="lg" onClick={() => scrollTo('#contact')} data-id="element-165" data-mp="145lp9a8jbp00">
              お問い合わせ
            </Button>
            <Button variant="ghost" size="lg" onClick={() => scrollTo('#ai-staff')} className="text-white hover:bg-white/10" data-id="element-166" data-mp="1e2goxwgjnvfv">
              サービスを見る
            </Button>
          </div>
        </div>

        {/* Cinematic console: central live-call AI core + orbiting glass badges */}
        <div ref={stageRef} onMouseMove={handleMove} onMouseLeave={handleLeave} className="relative mx-auto aspect-square w-full max-w-md" style={{
        perspective: 1000
      }} aria-hidden="true" data-id="element-167" data-mp="2thxg6oc5seg8">
          <motion.div className="relative h-full w-full" style={reduce ? undefined : {
          rotateX,
          rotateY,
          transformStyle: 'preserve-3d'
        }} data-id="element-168" data-mp="13f5611hsnvlc">
            {/* orbit rings */}
            <motion.div className="absolute inset-[10%] rounded-full border border-white/10" animate={reduce ? undefined : {
            rotate: 360
          }} transition={{
            duration: 40,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-169" data-mp="kgsz7mecuxsx" />
            <div className="absolute inset-[24%] rounded-full border border-white/10" data-id="element-170" data-mp="1ynpkupilicuv" />

            {/* orbiting glass task badges */}
            {ORBIT_BADGES.map(b => {
            const rad = b.angle * Math.PI / 180;
            const r = 42 * b.radius; // percentage from center
            const left = 50 + r * Math.cos(rad);
            const top = 50 + r * Math.sin(rad);
            return <motion.div key={b.label} className="absolute -translate-x-1/2 -translate-y-1/2" style={{
              left: `${left}%`,
              top: `${top}%`,
              translateZ: 60
            }} animate={reduce ? undefined : {
              y: [0, -6, 0]
            }} transition={{
              duration: 4 + b.angle / 90,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-171" data-mp="2id9g0x5rfmkf">
                  <div className="flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-2 shadow-lg backdrop-blur-md" data-id="element-172" data-mp="1om6qepwxx10x">
                    <span className={b.accent} data-id="element-173" data-mp="1jppzjnycw8k5">{b.icon}</span>
                    <span className="whitespace-nowrap text-xs font-medium text-white" data-id="element-174" data-mp="1eqd3ccllhp4w">
                      {b.label}
                    </span>
                  </div>
                </motion.div>;
          })}

            {/* central live-call core */}
            <div className="absolute left-1/2 top-1/2 w-[62%] -translate-x-1/2 -translate-y-1/2" style={{
            transform: 'translate(-50%, -50%) translateZ(90px)'
          }} data-id="element-175" data-mp="22lzdmb99cz45">
              <div className="rounded-2xl border border-white/15 bg-white/10 p-5 shadow-2xl backdrop-blur-xl" data-id="element-176" data-mp="2v4mvlhsyjy10">
                {/* status header */}
                <div className="flex items-center gap-3" data-id="element-177" data-mp="2ylhjskabokvm">
                  <span className="relative inline-flex h-11 w-11 items-center justify-center rounded-full bg-sky-blue/25 text-white" data-id="element-178" data-mp="2yn2eqb0ju5u0">
                    <PhoneCallIcon className="h-5 w-5" data-id="element-179" data-mp="37lxjrjrux9vq" />
                    {!reduce && <motion.span className="absolute inset-0 rounded-full border border-sky-blue" animate={{
                    scale: [1, 1.5],
                    opacity: [0.6, 0]
                  }} transition={{
                    duration: 1.6,
                    repeat: Infinity,
                    ease: 'easeOut'
                  }} data-id="element-180" data-mp="3ckh8tt1qz1sb" />}
                  </span>
                  <div className="min-w-0" data-id="element-181" data-mp="460bxhh64e0k">
                    <p className="truncate text-sm font-bold text-white" data-id="element-182" data-mp="434c1nlrff9h">
                      AI受付スタッフ
                    </p>
                    <p className="flex items-center gap-1.5 text-xs text-white/70" data-id="element-183" data-mp="1wursiafh8e32">
                      <span className="inline-block h-2 w-2 rounded-full bg-sage-green" data-id="element-184" data-mp="20vk43abtmv2i" />
                      対応中・24時間オンライン
                    </p>
                  </div>
                </div>

                {/* live waveform */}
                <div className="mt-4 flex items-center justify-between gap-3 rounded-xl bg-deep-navy/40 px-4 py-3" data-id="element-185" data-mp="2amohi5lum7v8">
                  <Waveform data-id="element-186" data-mp="2fk7w56mgkmnp" />
                  <span className="text-xs font-medium tabular-nums text-white/70" data-id="element-187" data-mp="2fn2feutqk1fj">
                    00:12
                  </span>
                </div>

                <p className="mt-3 text-xs leading-relaxed text-white/80" data-id="element-188" data-mp="2ompj5n9xtjbr">
                  「お電話ありがとうございます。ご用件をお伺いします。」
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}